import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule, DatePipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatChipsModule } from '@angular/material/chips'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslatePipe } from '@ngx-translate/core'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { Call, AdminAgendaMetadata, GoverningBodyType, CallType } from '../calls.service'
import { DatetimePickerComponent } from '../common/datetime-picker/datetime-picker.component'
import { SelectComponent, SelectOption } from '../common/select/select.component'
import {
  MAXIMUM_CALL_DURATION_IN_MINUTES,
  MINIMUM_CALL_DURATION_IN_MINUTES,
  CALL_DURATION_INCREMENT_IN_MINUTES,
} from '../add-call-page/add-call-page.component'
import { isDateTooFarInPastForManualCallCreation } from '../utils'
import { ToastService } from '../toast.service'

export type MeetingDetailsFormData = {
  date: Date | null
  duration: number
  location: string | null
  companyName: string | null
  governingBodyType: GoverningBodyType | null
  committeeType: string | null
  entities: string[]
  newEntity: string
  link: string | null
}

export type MeetingDetailsSaveData = {
  metadata: {
    location: string | null
    companyName: string | null
    governingBodyType: GoverningBodyType | null
    committeeType: string | null
    entities: string[]
  }
  dateTime: {
    date: Date
    endDate: Date
  } | null
  link: string | null
}

@Component({
  selector: 'app-meeting-details-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatTooltip,
    TranslatePipe,
    DatePipe,
    DatetimePickerComponent,
    SelectComponent,
    MatIconButtonSizesModule,
  ],
  templateUrl: './meeting-details-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-details-card.component.scss',
})
export class MeetingDetailsCardComponent {
  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  metadata!: AdminAgendaMetadata

  @Input()
  canEdit: boolean = false

  @Input()
  title: string | null = null

  @Input()
  stepNumber: number | null = null

  @Input()
  description: string | null = null

  @Output()
  detailsSaved = new EventEmitter<MeetingDetailsSaveData>()

  isEditingDetails = false
  editedDetailsFormData: MeetingDetailsFormData = {
    date: null,
    duration: 0,
    location: null,
    companyName: null,
    governingBodyType: null,
    committeeType: null,
    entities: [],
    newEntity: '',
    link: null,
  }

  governingBodyTypeOptions: SelectOption<GoverningBodyType>[] = Object.values(GoverningBodyType).map((type) => ({
    value: type,
    labelKey: `CALL_SETUP.CALL_DETAILS.GOVERNING_BODY_TYPE.${type}`,
  }))

  get canEditDateTime(): boolean {
    return this.call.isUpcoming && this.call.type !== 'SCHEDULED'
  }

  get increaseDurationButtonDisabled(): boolean {
    return this.editedDetailsFormData.duration >= MAXIMUM_CALL_DURATION_IN_MINUTES || !this.canEditDateTime
  }

  get decreaseDurationButtonDisabled(): boolean {
    return this.editedDetailsFormData.duration <= MINIMUM_CALL_DURATION_IN_MINUTES || !this.canEditDateTime
  }

  get isUploadedCall(): boolean {
    return this.call.type === CallType.AUDIO_UPLOAD || this.call.type === CallType.VIDEO_UPLOAD
  }

  get isLiveCall(): boolean {
    return this.call.type === CallType.LIVE
  }

  get canEditLink(): boolean {
    return this.call.isUpcoming
  }

  isDateTooFarInPast(date: Date | null): boolean {
    if (!this.isLiveCall) {
      return false
    }
    return isDateTooFarInPastForManualCallCreation(date)
  }

  get isLinkValid(): boolean {
    if (this.isUploadedCall || !this.canEditLink) {
      return true
    }
    const link = this.editedDetailsFormData.link?.trim() ?? ''
    return link !== ''
  }

  get isFormValid(): boolean {
    return this.isLinkValid && !this.isDateTooFarInPast(this.editedDetailsFormData.date)
  }

  startEditDetails() {
    const startDate = new Date(this.call.date)
    const endDate = new Date(this.call.endDate)
    const durationInMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60))

    this.editedDetailsFormData = {
      date: startDate,
      duration: durationInMinutes,
      location: this.metadata.location,
      companyName: this.metadata.companyName,
      governingBodyType: this.metadata.governingBodyType,
      committeeType: this.metadata.committeeType,
      entities: [...this.metadata.entities],
      newEntity: '',
      link: this.call.link,
    }
    this.isEditingDetails = true
  }

  cancelEditDetails() {
    this.isEditingDetails = false
    this.editedDetailsFormData = {
      date: null,
      duration: 0,
      location: null,
      companyName: null,
      governingBodyType: null,
      committeeType: null,
      entities: [],
      newEntity: '',
      link: null,
    }
  }

  saveDetails() {
    const saveData: MeetingDetailsSaveData = {
      metadata: {
        location: this.editedDetailsFormData.location,
        companyName: this.editedDetailsFormData.companyName,
        governingBodyType: this.editedDetailsFormData.governingBodyType,
        committeeType:
          this.editedDetailsFormData.governingBodyType === GoverningBodyType.COMMITTEE ? this.editedDetailsFormData.committeeType?.trim() || null : null,
        entities: this.editedDetailsFormData.entities,
      },
      dateTime: null,
      link: this.canEditLink ? this.editedDetailsFormData.link : this.call.link,
    }

    if (this.editedDetailsFormData.date !== null) {
      const originalDate = new Date(this.call.date)
      const originalEndDate = new Date(this.call.endDate)
      const originalDuration = Math.round((originalEndDate.getTime() - originalDate.getTime()) / (1000 * 60))

      const dateChanged = this.editedDetailsFormData.date.getTime() !== originalDate.getTime()
      const durationChanged = this.editedDetailsFormData.duration !== originalDuration

      if (dateChanged || durationChanged) {
        const newEndDate = new Date(this.editedDetailsFormData.date.getTime() + this.editedDetailsFormData.duration * 60 * 1000)
        saveData.dateTime = {
          date: this.editedDetailsFormData.date,
          endDate: newEndDate,
        }
      }
    }

    this.detailsSaved.emit(saveData)
    this.isEditingDetails = false
  }

  increaseDuration() {
    if (this.editedDetailsFormData.duration < MAXIMUM_CALL_DURATION_IN_MINUTES) {
      this.editedDetailsFormData.duration += CALL_DURATION_INCREMENT_IN_MINUTES
    }
  }

  decreaseDuration() {
    if (this.editedDetailsFormData.duration > MINIMUM_CALL_DURATION_IN_MINUTES) {
      this.editedDetailsFormData.duration -= CALL_DURATION_INCREMENT_IN_MINUTES
    }
  }

  addEntity() {
    const trimmedEntity = this.editedDetailsFormData.newEntity.trim()
    if (trimmedEntity !== '' && !this.editedDetailsFormData.entities.includes(trimmedEntity)) {
      this.editedDetailsFormData.entities = [...this.editedDetailsFormData.entities, trimmedEntity]
      this.editedDetailsFormData.newEntity = ''
    }
  }

  removeEntity(entity: string) {
    this.editedDetailsFormData.entities = this.editedDetailsFormData.entities.filter((item) => item !== entity)
  }

  private toastService = inject(ToastService)

  copyLink() {
    if (this.call.link === null) {
      return
    }
    navigator.clipboard.writeText(this.call.link)
    this.toastService.show('COMMON.COPIED_TO_CLIPBOARD')
  }
}
